import React, { useEffect, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import styled, { css } from 'styled-components';
import HelpButton from '~/components/Buttons/HelpButton';
import { ANIMATION_CONFIG } from '~/scenes/Settings/constants';
import { FONT_SIZE_OF_18 } from '~/styles/constants';

type Props = {
  title?: string;
  dataTestid?: string;
  className?: string;

  /** If the normal layout helpers for flex layouts should be ignored */
  manualLayout?: boolean;
  dataIsPolling?: boolean;
};

const DetailsBlock: React.FC<Props> = ({
  children,
  dataTestid,
  title,
  dataIsPolling,
  manualLayout,
  ...rest
}) => {
  const [animationFinished, setAnimationFinished] = useState(false);

  const animation = useSpring({
    ...ANIMATION_CONFIG,
    onRest: ({ finished }) => {
      if (finished) setAnimationFinished(true);
    },
  });

  const [styleTransform, setStyleTransform] = useState<any>(
    animation.transform,
  );

  useEffect(() => {
    if (animationFinished) {
      setStyleTransform(undefined);
    }
  }, [animationFinished]);

  const style = { ...animation, transform: styleTransform };

  return (
    <Container
      {...rest}
      $manualLayout={manualLayout}
      data-testid={dataTestid ? dataTestid : ''}
      data-ispolling={dataIsPolling}
      style={style}
    >
      {title && <SectionTitle>{title}</SectionTitle>}
      <animated.div style={style}>{children}</animated.div>
    </Container>
  );
};

export const SectionTitle = styled.div<{}>`
  display: flex;
  font-weight: bold;

  ${({ theme }) => css`
    color: ${theme.color('primary')};
    font-size: ${FONT_SIZE_OF_18}px;
  `}
`;

export const SectionHelpButton = styled(HelpButton)<{}>`
  ${({ theme }) => css`
    margin-left: ${theme.space('s')};
    margin-bottom: ${theme.space('s')};
    font-size: ${theme.fontSize('m')};
  `}
`;

type ContainerProps = {
  $manualLayout?: boolean;
};

const MIN_CARD_WIDTH = 350;
const MAX_CARD_WIDTH = 530;

const Container = styled(animated.div)<ContainerProps>`
  flex-grow: 1;
  width: 100%;

  ${({ theme }) => css`
    background-color: ${theme.color('white')};
    border: ${theme.getTokens().border.width.s} solid
      ${theme.color('white', 'dark')};
    border-radius: ${theme.getTokens().border.radius.s};
    padding: ${theme.space('l')};
    box-shadow: ${theme.getTokens().boxShadow.base};
  `}

  ${({ theme, $manualLayout }) => {
    if ($manualLayout !== true) {
      return css`
        min-width: ${MIN_CARD_WIDTH}px;
        max-width: ${MAX_CARD_WIDTH}px;
        margin-bottom: ${theme.space('l')};
      `;
    }
    return ``;
  }}
`;

export default DetailsBlock;
